'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { RequestFormData } from './ServiceRequestWizard';

interface StepTwoProps {
  formData: RequestFormData;
  updateFormData: (updates: Partial<RequestFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const mockUploadedPhotos = [
  { id: 'photo-001', name: 'front-yard.jpg', size: '2.1 MB' },
  { id: 'photo-002', name: 'side-gate.jpg', size: '1.8 MB' },
];

export default function StepTwo({ formData, updateFormData, onNext, onBack }: StepTwoProps) {
  const [uploadedPhotos, setUploadedPhotos] = useState(mockUploadedPhotos);
  const [dragOver, setDragOver] = useState(false);

  const canProceed =
    !!(formData.propertyAddress && formData.preferredDate1 && formData.preferredTime);

  const removePhoto = (id: string) => {
    setUploadedPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    // Backend integration point: upload files to /api/uploads
    const newPhoto = { id: `photo-${Date.now()}`, name: 'uploaded-file.jpg', size: '1.2 MB' };
    setUploadedPhotos((prev) => [...prev, newPhoto]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Property & scheduling details</h2>
        <p className="text-sm text-muted-foreground mt-1">Tell us where the job is and when you'd like it done.</p>
      </div>

      {/* Property address */}
      <div>
        <label htmlFor="propertyAddress" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
          Property Address <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">The address where the work will be performed.</p>
        <input
          id="propertyAddress"
          type="text"
          value={formData.propertyAddress ?? ''}
          onChange={(e) => updateFormData({ propertyAddress: e.target.value })}
          placeholder="e.g. 14 Banksia Drive, Elanora, QLD 4221"
          className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* Property access */}
      <div>
        <label htmlFor="propertyAccess" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
          Property Access
        </label>
        <p className="text-xs text-muted-foreground mb-2">How will our team access the property?</p>
        <select
          id="propertyAccess"
          value={formData.propertyAccess ?? ''}
          onChange={(e) => updateFormData({ propertyAccess: e.target.value })}
          className="w-full sm:w-80 px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">Select access method</option>
          <option value="home">I'll be home to let them in</option>
          <option value="key-lockbox">Key lockbox — code provided on booking</option>
          <option value="key-leave">Key left under mat / with neighbour</option>
          <option value="gate-code">Gated property — code provided</option>
          <option value="real-estate">Via real estate agent</option>
          <option value="other">Other — I'll add details in notes</option>
        </select>
      </div>

      {/* Preferred dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="preferredDate1" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
            Preferred Date <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">Your first preference.</p>
          <input
            id="preferredDate1"
            type="date"
            value={formData.preferredDate1 ?? ''}
            onChange={(e) => updateFormData({ preferredDate1: e.target.value })}
            className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div>
          <label htmlFor="preferredDate2" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
            Alternative Date
          </label>
          <p className="text-xs text-muted-foreground mb-2">Optional second preference.</p>
          <input
            id="preferredDate2"
            type="date"
            value={formData.preferredDate2 ?? ''}
            onChange={(e) => updateFormData({ preferredDate2: e.target.value })}
            className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Preferred time */}
      <div>
        <label htmlFor="preferredTime" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
          Preferred Start Time <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
          {[
            { id: 'morning', label: 'Morning', sub: '7am – 12pm' },
            { id: 'midday', label: 'Midday', sub: '12pm – 2pm' },
            { id: 'afternoon', label: 'Afternoon', sub: '2pm – 5pm' },
            { id: 'flexible', label: 'Flexible', sub: 'Any time' },
          ].map((t) => (
            <button
              key={`time-${t.id}`}
              type="button"
              onClick={() => updateFormData({ preferredTime: t.id })}
              className={`flex flex-col items-center gap-0.5 px-3 py-2.5 rounded-lg border-2 text-center transition-all duration-150 ${
                formData.preferredTime === t.id
                  ? 'bg-secondary border-primary' :'bg-muted/30 border-border hover:border-primary/40'
              }`}
            >
              <span className={`text-sm font-semibold ${formData.preferredTime === t.id ? 'text-primary' : 'text-foreground'}`}>{t.label}</span>
              <span className="text-xs text-muted-foreground">{t.sub}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Special instructions */}
      <div>
        <label htmlFor="specialInstructions" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
          Special Instructions or Notes
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          Pets on property, areas to avoid, parking instructions, specific products required, etc.
        </p>
        <textarea
          id="specialInstructions"
          value={formData.specialInstructions ?? ''}
          onChange={(e) => updateFormData({ specialInstructions: e.target.value })}
          rows={3}
          placeholder="e.g. Please use fragrance-free products — we have a family member with allergies. Dog is friendly but will bark. Parking available in driveway."
          className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
      </div>

      {/* Photo upload */}
      <div>
        <p className="text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">Photos (Optional)</p>
        <p className="text-xs text-muted-foreground mb-3">
          Upload photos of the area to be serviced — helps us provide a more accurate quote.
        </p>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-150 ${
            dragOver ? 'border-primary bg-secondary' : 'border-border hover:border-primary/40 hover:bg-muted/20'
          }`}
        >
          <Icon name="PhotoIcon" size={28} variant="outline" className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-medium text-foreground">Drop photos here or</p>
          <label className="mt-1 inline-block cursor-pointer">
            <span className="text-sm text-primary font-semibold hover:underline">browse files</span>
            <input type="file" multiple accept="image/*" className="hidden" onChange={() => {
              // Backend integration point: upload to /api/uploads
            }} />
          </label>
          <p className="text-xs text-muted-foreground mt-1">JPG, PNG, HEIC up to 10MB each</p>
        </div>

        {uploadedPhotos.length > 0 && (
          <div className="mt-3 space-y-2">
            {uploadedPhotos.map((photo) => (
              <div key={photo.id} className="flex items-center justify-between px-3 py-2 bg-muted/40 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name="PhotoIcon" size={16} variant="outline" className="text-primary" />
                  <span className="text-xs font-medium text-foreground">{photo.name}</span>
                  <span className="text-xs text-muted-foreground">{photo.size}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removePhoto(photo.id)}
                  className="p-1 text-muted-foreground hover:text-red-500 transition-colors"
                  aria-label={`Remove ${photo.name}`}
                >
                  <Icon name="XMarkIcon" size={14} variant="outline" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-border text-sm font-medium text-muted-foreground rounded-lg hover:bg-muted hover:text-foreground transition-all duration-150"
        >
          <Icon name="ArrowLeftIcon" size={16} variant="outline" />
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue to Your Details
          <Icon name="ArrowRightIcon" size={16} variant="outline" />
        </button>
      </div>
    </div>
  );
}